import {Header as FuelHeader, Popover, PopoverButton, PopoverContent, HeaderActionArea} from '@deere/fuel-react';
import {Box, IconButton, Avatar, Divider, Typography, ListItemButton, ListItemIcon, ListItemText, MenuItem, Select, SelectChangeEvent} from '@mui/material';
import {AnchorLink} from './AnchorLink';
import {AccountCircleOutlined, ExitToAppOutlined, Home, Language} from '@mui/icons-material';
import {useNavigationItems} from 'hooks/useNavigationItems';
import {FC, useEffect, useRef, useState} from 'react';
import {useOktaAuth} from '@okta/okta-react';
import {useTranslation} from 'react-i18next';

const SIGNOUT_URL = 'https://sso.johndeere.com/login/default';

const LANGUAGES = [
    {
        code: 'english',
        label: 'English'
    },
    {
        code: 'portuguese',
        label: 'Portuguese'
    },
    {
        code: 'spanish',
        label: 'Spanish'
    },
    {
        code: 'french',
        label: 'French'
    },
    {
        code: 'german',
        label: 'German'
    }
];

const Header: FC<unknown> = () => {
    const {
        t, i18n
    } = useTranslation();

    const homeIcon = {
        href: '/',
        label: t('header.home'),
        selected: location.pathname === '/',
        startIcon: <Home/>
    };

    const navigationItems = [homeIcon, ...useNavigationItems()];

    const {
        authState, oktaAuth
    } = useOktaAuth();
    const [userInfo, setUserInfo] = useState<any>(null);
    const languageFetched = useRef(false);

    useEffect(() => {
        if (!authState || !authState.isAuthenticated) {
            // When user isn't authenticated, forget any user info
            setUserInfo(null);
        } else {
            setUserInfo(authState.idToken?.claims);
        }
    }, [authState, oktaAuth]); // Update if authState changes

    useEffect(() => {
        if (!authState?.accessToken?.accessToken) {
            return;
        }
        if (languageFetched.current) {
            return;
        }
        languageFetched.current = true;
        fetch(`${process.env.REACT_APP_BACKEND_URL}/user/language`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authState.accessToken.accessToken}`
            }
        })
            .then((res) => res.ok ? res.json() : null)
            .then((data) => {
                if (data?.language) {
                    i18n.changeLanguage(data.language);
                }
                return null;
            })
            .catch((error) => console.log('Failed to load language preference:', error));
    }, [authState, i18n]);

    const handleLanguageChange = (event: SelectChangeEvent): void => {
        const language = event.target.value;

        i18n.changeLanguage(language);
        if (!authState?.accessToken?.accessToken) {
            return;
        }
        fetch(`${process.env.REACT_APP_BACKEND_URL}/user/language?language=${language}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${authState.accessToken.accessToken}`
            }
        }).catch((error) => console.log('Failed to save language preference:', error));
    };

    return (
        <FuelHeader
            HeaderLogoLinkComponent={AnchorLink}
            // isContained={true}
            logoLink='/'
            navigationItems={navigationItems}
            title={t('app.title')}
            titleLink=''
        >
            <HeaderActionArea>
                <Box
                    alignItems='center'
                    display='flex'
                    mr={1}
                >
                    <Select
                        disableUnderline
                        // disabled
                        onChange={handleLanguageChange}
                        size='small'
                        startAdornment={
                            <Language
                                sx={{
                                    fontSize: 18,
                                    color: 'inherit',
                                    mr: 0.5
                                }}
                            />
                        }
                        sx={{
                            color: 'inherit',
                            fontSize: '13px',
                            '.MuiOutlinedInput-notchedOutline': {
                                border: 'none'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                border: 'none'
                            },
                            '.MuiSvgIcon-root': {
                                color: 'inherit'
                            },
                            minWidth: 120
                        }}
                        value={i18n.language.split('-')[0]}
                        variant='outlined'
                    >
                        {LANGUAGES.map((lang) => (
                            <MenuItem
                                key={lang.code}
                                value={lang.code}
                            >
                                {lang.label}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
                <Popover>
                    <PopoverButton
                        aria-label='Account'
                        component={IconButton}
                    ><AccountCircleOutlined/>
                    </PopoverButton>
                    <PopoverContent>
                        <Box minWidth='225px'>
                            <Box
                                alignItems='center'
                                display='flex'
                                p={4}
                            >
                                <Avatar data-fuel-size='small'>{userInfo?.firstName?.charAt(0)}{userInfo?.lastName?.charAt(0)}</Avatar>
                                <Box ml={3}>
                                    <Typography
                                        color='text.secondary'
                                        data-fuel-size={'sm'}
                                        fontWeight='600'
                                    >{userInfo?.firstName} {userInfo?.lastName}</Typography>
                                </Box>
                            </Box>
                            <Box>
                                <Divider/>
                                <ListItemButton
                                    sx={{
                                        pl: 5
                                    }}
                                >
                                    <ListItemIcon>
                                        <ExitToAppOutlined/>
                                    </ListItemIcon>
                                    <ListItemText
                                        onClick={(): void => {
                                            oktaAuth.signOut({
                                                postLogoutRedirectUri: SIGNOUT_URL
                                            });
                                        }}
                                        primary={t('header.signOut')}
                                        primaryTypographyProps={{
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            color: '#333'
                                        }}
                                    />
                                </ListItemButton>
                            </Box>
                        </Box>
                    </PopoverContent>
                </Popover>
            </HeaderActionArea>
        </FuelHeader>
    );
};

export default Header;
